// src/components/shell/CommandPalette.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal from '../Modal.mjs';
import { UserContext } from '../../context/UserContext';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { useTheme } from '../../context/ThemeContext';
import { NAV_ITEMS } from './navigation';
import { QUERY_PARAM } from './GlobalSearch';
import '../../css/CommandPalette.css';

/**
 * Case-insensitive subsequence match, so "tgs" finds "Go to Tags".
 *
 * The group comes first because that is the order the palette shows them in —
 * a heading above its items — which makes "go pro" read as a natural way to
 * ask for Go to → Projects.
 */
export function commandMatches(command, term) {
    if (!term) return true;
    const haystack = `${command.group} ${command.label} ${command.keywords ?? ''}`.toLowerCase();
    let i = 0;
    for (const char of term.toLowerCase()) {
        if (char === ' ') continue;
        i = haystack.indexOf(char, i);
        if (i === -1) return false;
        i += 1;
    }
    return true;
}

/**
 * Everything the app can do, one keystroke away.
 *
 * Ctrl/Cmd+K. Before this the only global keys were Ctrl+A and Escape, and
 * nothing anywhere told you a shortcut existed — which is a poor deal for the
 * people a digital asset manager is FOR, who live in the thing all day.
 */
export default function CommandPalette({ open, onClose }) {
    const navigate = useNavigate();
    const { user, logout } = useContext(UserContext);
    const { workspaces, activeWorkspace, setActiveWorkspace } = useContext(WorkspaceContext);
    const { resolvedTheme, toggleTheme } = useTheme();

    const [term, setTerm] = useState('');
    const [active, setActive] = useState(0);
    const listRef = useRef(null);

    const commands = useMemo(() => {
        const go = NAV_ITEMS
            .filter((item) => !item.adminOnly || user?.isPlatformAdmin)
            .map((item) => ({
                id: `go:${item.to}`,
                group: 'Go to',
                label: item.label,
                run: () => navigate(item.to),
            }));

        const switchTo = (workspaces || [])
            .filter((ws) => ws.id !== activeWorkspace?.id)
            .map((ws) => ({
                id: `ws:${ws.id}`,
                group: 'Switch workspace',
                label: ws.name,
                run: () => setActiveWorkspace(ws),
            }));

        return [
            ...go,
            { id: 'go:storage', group: 'Go to', label: 'Storage & usage', run: () => navigate('/workspace/storage') },
            ...switchTo,
            {
                id: 'theme',
                group: 'Appearance',
                label: `Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`,
                keywords: 'dark light mode',
                run: toggleTheme,
            },
            { id: 'sign-out', group: 'Account', label: 'Sign out', run: logout },
        ];
    }, [navigate, user, workspaces, activeWorkspace, setActiveWorkspace, resolvedTheme, toggleTheme, logout]);

    // Whatever doesn't match a command is still worth doing: search for it.
    const matches = useMemo(() => {
        const found = commands.filter((c) => commandMatches(c, term));
        if (!term.trim()) return found;
        return [
            ...found,
            {
                id: 'search',
                group: 'Search',
                label: `Search files for “${term.trim()}”`,
                run: () => navigate(`/?${QUERY_PARAM}=${encodeURIComponent(term.trim())}`),
            },
        ];
    }, [commands, term, navigate]);

    useEffect(() => { setActive(0); }, [term]);

    useEffect(() => {
        if (!open) return;
        setTerm('');
        setActive(0);
    }, [open]);

    if (!open) return null;

    const run = (command) => {
        // Close first: a command that navigates should not leave the palette
        // hanging over its destination.
        onClose();
        command?.run?.();
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            setActive((i) => (matches.length ? (i + 1) % matches.length : 0));
        } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            setActive((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            run(matches[active]);
        }
    };

    let lastGroup = null;

    return (
        <Modal className="qd-palette modal--chrome" onClose={onClose} aria-label="Command palette">
            <div className="qd-palette__field">
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <input
                    autoFocus
                    type="text"
                    className="qd-palette__input"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Type a command, or search…"
                    aria-label="Type a command, or search"
                    aria-controls="qd-palette-list"
                    aria-activedescendant={matches[active] ? `qd-cmd-${matches[active].id}` : undefined}
                    role="combobox"
                    aria-expanded="true"
                />
            </div>

            <ul className="qd-palette__list" id="qd-palette-list" role="listbox" aria-label="Commands" ref={listRef}>
                {matches.map((command, index) => {
                    const heading = command.group !== lastGroup ? command.group : null;
                    lastGroup = command.group;
                    return (
                        <React.Fragment key={command.id}>
                            {heading && <li className="qd-palette__group" role="presentation">{heading}</li>}
                            <li
                                id={`qd-cmd-${command.id}`}
                                role="option"
                                aria-selected={index === active}
                                className={`qd-palette__item ${index === active ? 'is-active' : ''}`}
                                onMouseEnter={() => setActive(index)}
                                onClick={() => run(command)}
                            >
                                {command.label}
                            </li>
                        </React.Fragment>
                    );
                })}
                {matches.length === 0 && <li className="qd-palette__empty" role="presentation">Nothing matches that.</li>}
            </ul>
        </Modal>
    );
}
